import { addButtonStyles } from '@components/Customization/PoiDesigner/designer.styles';
import {
  sameTarget, type DraftIssue, type DraftTarget, type PageSection, type ReportDesignDraft,
} from '@lib/customization/reportDesign';
import { useLocalization } from '@lib/localization';
import { Box, Button, ButtonBase, Stack, Typography } from '@mui/material';
import { reportDesignLimits } from '@store/api/reportDesignEndpoints';
import {
  ClipboardListIcon, FileAltIcon, InfoCircleIcon, ListUlIcon, PaletteIcon, PlusIcon, WindowMaximizeIcon,
  WindowMinimizeIcon,
} from 'react-line-awesome';
import { entrySelectedStyles, entryStyles, groupTitleStyles, issueDotStyles } from './designer.styles';
import { pageLabel } from './describe';
import { CHECKPOINT_TEMPLATES, SUMMARY_TEMPLATES } from './options';

/** Everything the sections list can ask of the designer. */
export interface StructureActions {
  select: (target: DraftTarget) => void;
  addPage: (section: PageSection) => void;
}

interface Props {
  draft: ReportDesignDraft;
  selection: DraftTarget;
  canEdit: boolean;
  /** What stands in the way of a save: whatever one is about is marked in the list. */
  issues: DraftIssue[];
  actions: StructureActions;
}

/**
 * The sections of a design from top to bottom, in the order it prints: what holds for the
 * whole report, the cover pages, the summary and the checkpoints, the closing pages.
 * Selecting an entry opens its settings in the inspector and brings it into view on the
 * canvas. It is also the way to what the canvas has no place for: the design's name, its
 * theme, a header or a footer that is switched off.
 *
 * A page is one entry here. Its blocks are on the canvas, where they are composed.
 *
 * Selecting is not editing: a reader who may not change the design walks the same list.
 */
const StructurePanel: React.FC<Props> = ({ draft, selection, canEdit, issues, actions }) => {
  const { t } = useLocalization();

  const isSelected = (target: DraftTarget) => sameTarget(selection, target);
  const isFlagged = (target: DraftTarget) => issues.some((issue) => sameTarget(issue.target, target));

  const templateLabel = (options: { value: string; label: string }[], value: string) => {
    const option = options.find((candidate) => candidate.value === value);

    return option ? t(option.label) : value;
  };

  const entry = (target: DraftTarget, icon: React.ReactNode, label: string, detail?: string) => (
    <ButtonBase
      onClick={() => actions.select(target)}
      aria-current={isSelected(target) || undefined}
      sx={isSelected(target) ? [entryStyles, entrySelectedStyles] : entryStyles}
    >
      {icon}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{label}</Typography>
        {detail && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{detail}</Typography>
        )}
      </Box>
      {isFlagged(target) && <Box sx={issueDotStyles} />}
    </ButtonBase>
  );

  /** The covers or the closings: an entry a page, then the way to add one. */
  const pages = (section: PageSection) => (
    <>
      <Typography sx={groupTitleStyles}>
        {t(section === 'covers' ? 'ReportDesign_Section_Covers' : 'ReportDesign_Section_Closings')}
      </Typography>

      {draft[section].length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.25, pb: 0.5 }}>
          {t(section === 'covers' ? 'ReportDesign_NoCovers' : 'ReportDesign_NoClosings')}
        </Typography>
      )}

      {draft[section].map((page) => (
        <Box key={page.id}>
          {entry(
            { kind: 'page', id: page.id }, <FileAltIcon />, pageLabel(draft, page.id, t),
            t('ReportDesign_Block_Count', { count: page.blocks.length }),
          )}
        </Box>
      ))}

      <Box>
        <Button
          size="small"
          startIcon={<PlusIcon />}
          sx={addButtonStyles}
          disabled={!canEdit || draft[section].length >= reportDesignLimits.pages}
          onClick={() => actions.addPage(section)}
        >
          {t('ReportDesign_Page_Add')}
        </Button>
      </Box>
    </>
  );

  // A strip that is switched off says so in the words the templates use for "not printed".
  const stripDetail = (enabled: boolean) => (enabled ? undefined : t('ReportDesign_Template_None'));

  return (
    <Stack spacing={0.25}>
      {entry({ kind: 'details' }, <InfoCircleIcon />, t('ReportDesign_Section_Details'), draft.name)}
      {entry({ kind: 'theme' }, <PaletteIcon />, t('ReportDesign_Section_Theme'))}
      {entry({ kind: 'header' }, <WindowMaximizeIcon />, t('ReportDesign_Section_Header'), stripDetail(draft.header.enabled))}
      {entry({ kind: 'footer' }, <WindowMinimizeIcon />, t('ReportDesign_Section_Footer'), stripDetail(draft.footer.enabled))}

      {pages('covers')}

      <Typography sx={groupTitleStyles}>{t('ReportDesign_Section_Results')}</Typography>
      {entry(
        { kind: 'summary' }, <ClipboardListIcon />, t('Common_Summary'),
        templateLabel(SUMMARY_TEMPLATES, draft.summary.template),
      )}
      {entry(
        { kind: 'checkpoints' }, <ListUlIcon />, t('Common_Checkpoints'),
        templateLabel(CHECKPOINT_TEMPLATES, draft.checkpoints.template),
      )}

      {pages('closings')}
    </Stack>
  );
};

export default StructurePanel;
